import React, { useEffect, useState, useRef } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import VehicleReadingService from "../services/vehicleReadingService";
import VehicleRegistrationService from "../services/vehicleRegistrationService";
import ProjectService from "../services/ProjectService";
import ReadingTypeService from "../services/ReadingTypeService";
import Breadcrumb from "../components/Breadcrumb";
import StorageService from "../services/StorageService";
import utility from "../utils/utility";
import { configSettings } from "../config/configSettings";
import MessageDialog from "../components/MessageDialog";
import moment from "moment";


export default function AddVehicleReading() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [msgType, setMsgType] = useState("error");
  // const messageTimer = useRef(null);
  const [editMode, setEditMode] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [readingId, setReadingId] = useState(null);
  const [originalReadingDate, setOriginalReadingDate] = useState("");

  const [projects, setProjects] = useState([]);
  const [vehicles, setVehicles] = useState([]);

  const [fromPreview, setFromPreview] = useState(null);
  const [toPreview, setToPreview] = useState(null);
  const [fromPreviewOld, setFromPreviewOld] = useState(null);
  const [toPreviewOld, setToPreviewOld] = useState(null);

  const [readingTypes, setReadingTypes] = useState([]);

  const [duplicateWarning, setDuplicateWarning] = useState("");
  const [statusMismatchError, setStatusMismatchError] = useState("");
  const [existingStatus, setExistingStatus] = useState("");
  const [lastReadingError, setLastReadingError] = useState("");

  const checkLastReadingStatus = async (vehicleId, projectId) => {
    if (!vehicleId || !projectId) {
      setLastReadingError("");
      return;
    }
    try {
      const companyId = StorageService.activeCompany().companyId;
      const res = await VehicleReadingService.checkLastCompleted(
        vehicleId,
        projectId,
        companyId,
        editMode ? id : null
      );
      if (res?.status && !res.isCompleted) {
        const errorMsg = res.message || "The previous reading for this vehicle and project is missing its End Reading.";
        setLastReadingError(errorMsg);
        if (workingStatus === "Working") {
          showMessage(errorMsg, "error");
        }
      } else {
        setLastReadingError("");
      }
    } catch (err) {
      console.error("Error checking last reading status:", err);
      setLastReadingError("");
    }
  };

  const checkDuplicateReading = async (vehicleId, date, selectedStatus) => {
    if (!vehicleId || !date || (editMode && date === originalReadingDate)) {
      setDuplicateWarning("");
      setStatusMismatchError("");
      setExistingStatus("");
      return;
    }
    const vehicle = vehicles.find((v) => String(v.id) === String(vehicleId));
    if (!vehicle) {
      setDuplicateWarning("");
      setStatusMismatchError("");
      setExistingStatus("");
      return;
    }
    try {
      const companyId = StorageService.activeCompany().companyId;
      const res = await VehicleReadingService.checkExisting(vehicleId, date, companyId);
      if (res?.exists) {
        const dupMsg = `This vehicle's hire type is ${vehicle.leaseType || 'N/A'}, and a reading has already been created for this date.`;
        setDuplicateWarning(dupMsg);
        showMessage(dupMsg, "warning");
        setExistingStatus(res.workingStatus);

        if (selectedStatus) {
          if (selectedStatus.toLowerCase() !== (res.workingStatus || "").toLowerCase()) {
            const mismatchMsg = `Only one working status is allowed per day. The existing reading has status '${res.workingStatus}'.`;
            setStatusMismatchError(mismatchMsg);
            showMessage(mismatchMsg, "error");
          } else {
            setStatusMismatchError("");
          }
        } else {
          setStatusMismatchError("");
        }
      } else {
        setDuplicateWarning("");
        setStatusMismatchError("");
        setExistingStatus("");
      }
    } catch (err) {
      console.error("Error checking duplicate reading:", err);
      setDuplicateWarning("");
      setStatusMismatchError("");
      setExistingStatus("");
    }
  };

  const today = new Date().toISOString().slice(0, 10);


  const getNowLocalDateTime = () => {
    const now = new Date();
    const offset = now.getTimezoneOffset();
    const local = new Date(now.getTime() - offset * 60000);
    return local.toISOString().slice(0, 16);
  };

  const getDateTimeForDate = (dateStr) => {
    const baseDate = dateStr || new Date().toISOString().slice(0, 10);
    const now = new Date();
    const offset = now.getTimezoneOffset();
    const local = new Date(now.getTime() - offset * 60000);
    const timePart = local.toISOString().slice(11, 16);
    return `${baseDate}T${timePart}`;
  };

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    getValues,
    watch,
    control,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      readingDate: today,
      workingStatus: "",
      workDescription: "",
      driverName: "",
      readings: [
        {
          fromReading: "",
          toReading: "",
          startDateTime: getDateTimeForDate(today),
          endDateTime: "",
        },
      ],
    },
  });

  // const {
  //   register,
  //   handleSubmit,
  //   reset,
  //   setValue,
  //   watch,
  //   control,
  //   formState: { errors },
  // } = useForm({
  //   mode: "onChange",
  //   defaultValues: {
  //     readings: [
  //       { fromReading: "", toReading: "", startDateTime: "", endDateTime: "" },
  //     ],
  //   },
  // });

  const { fields, prepend, remove } = useFieldArray({
    control,
    name: "readings",
  });

  const selectedProjectId = watch("projectId");
  const readingTypeId = watch("readingTypeId");

  const workingStatus = watch("workingStatus");
  const isNotWorking = workingStatus && workingStatus !== "Working";
  const isDescriptionRequired = workingStatus === "Idle" || workingStatus === "BreakDown";

  /* ---------------- DATE FORMAT FIX ---------------- */
  const formatDateTimeLocal = (value) => {
    if (!value) return "";
    return moment.utc(value).format("YYYY-MM-DDTHH:mm");
  };
  /* -------------------------------------------------- */

  const showMessage = (text, type = "error") => {
    setMessage(text);
    setMsgType(type);
    // if (messageTimer.current) clearTimeout(messageTimer.current);
    // messageTimer.current = setTimeout(() => setMessage(""), 4000);
  };

  // const closeMessage = () => {
  //   setMessage("");
  //   if (messageTimer.current) clearTimeout(messageTimer.current);
  // };

  // Load projects
  const loadProjects = async () => {
    try {
      const companyId = StorageService.activeCompany().companyId;
      const employeeId = StorageService.loginData().employeeId;
      const res = await ProjectService.getActiveProjectsByEmployee(
        companyId,
        employeeId
      );
      setProjects(res?.data || res || []);
    } catch {
      setProjects([]);
    }
  };

  const fetchVehicles = async (projectId, targetDate = null) => {
    if (!projectId) {
      setVehicles([]);
      return;
    }
    try {
      const dateToUse = targetDate || getValues("readingDate") || today;
      const res = await VehicleRegistrationService.getByProjectId(projectId, false, dateToUse);
      const vehicleList = res?.status ? res.data : res?.data || res || [];
      setVehicles(vehicleList);

      const currentVehicleId = getValues("vehicleId");
      if (currentVehicleId && !vehicleList.some((v) => String(v.id) === String(currentVehicleId))) {
        setValue("vehicleId", "", { shouldValidate: true });
        setValue("readingTypeId", "", { shouldValidate: true });
      }
    } catch {
      setVehicles([]);
    }
  };

  const handleVehicleChange = (vehicleId) => {
    setValue("vehicleId", vehicleId, { shouldValidate: true });
    const vehicle = vehicles.find((v) => String(v.id) === String(vehicleId));
    if (vehicle) {
      const match = readingTypes.find(
        (rt) => rt.name?.toLowerCase() === vehicle.readingType?.toLowerCase()
      );
      if (match) {
        setValue("readingTypeId", String(match.id), { shouldValidate: true });
      } else {
        setValue("readingTypeId", "", { shouldValidate: true });
      }
    } else {
      setValue("readingTypeId", "", { shouldValidate: true });
    }
  };

  const normalizeReadingTypes = (raw) => {
    if (!Array.isArray(raw)) return [];
    return raw.map((rt) => ({
      id: String(rt.id ?? rt.readingTypeId),
      name: String(rt.readingType ?? rt.name ?? ""),
    }));
  };

  const fetchReadingTypes = async () => {
    try {
      const res = await ReadingTypeService.getActive();
      const data = Array.isArray(res?.data) ? res.data : res || [];
      setReadingTypes(normalizeReadingTypes(data));
    } catch {
      setReadingTypes([]);
    }
  };

  const getReadingTypeName = (id) => {
    const rt = readingTypes.find((r) => String(r.id) === String(id));
    return rt?.name || "";
  };

  const loadEditData = async () => {
    await loadProjects();
    await fetchReadingTypes();

    // if (!id) {
    //   reset({
    //     status: "Active",
    //     readings: [
    //       {
    //         fromReading: "",
    //         toReading: "",
    //         startDateTime: "",
    //         endDateTime: "",
    //       },
    //     ],
    //   });
    //   return;
    // }

    if (!id) {
      const today = new Date().toISOString().slice(0, 10);
      reset({
        readingDate: today,   // ✅ default to today
        status: "Active",
        workingStatus: "",
        workDescription: "",
        driverName: "",
        readings: [
          {
            fromReading: "",
            toReading: "",
            startDateTime: getDateTimeForDate(today),
            endDateTime: "",
          },
        ],
      });
      return;
    }

    setEditMode(true);
    setReadingId(id);
    setEditLoading(true);

    try {
      const res = await VehicleReadingService.getById(id);
      const data = res?.data;
      if (!data) return;

      const dateSlice = data.readingDate?.slice(0, 10);
      setOriginalReadingDate(dateSlice || "");

      await fetchVehicles(data.projectId, dateSlice);

      const normalizedReadings = (data.readings || [])
        .filter((r) => r.status?.toLowerCase() === "active") // ONLY ACTIVE
        .slice()
        .reverse()
        .map((r) => ({
          id: r.id,
          fromReading: r.fromReading ?? "",
          toReading: r.toReading ?? "",
          startDateTime: r.startTime ? formatDateTimeLocal(r.startTime) : "",
          endDateTime: r.endTime ? formatDateTimeLocal(r.endTime) : "",
        }));
      reset({
        readingDate: dateSlice,
        projectId: String(data.projectId),
        vehicleId: String(data.vehicleId),
        readingTypeId: String(data.readingTypeId),

        workingStatus: data.workingStatus ?? data.readings?.[0]?.workingStatus ?? "",
        workDescription: data.workDescription ?? data.readings?.[0]?.workDescription ?? "",
        driverName: data.driverName ?? data.readings?.[0]?.driverName ?? "",

        readings: normalizedReadings.length
          ? normalizedReadings
          : [
            {
              fromReading: "",
              toReading: "",
              startDateTime: "",
              endDateTime: "",
            },
          ],
      });

      const firstReading = data.readings?.[0];
      if (firstReading?.fromReadingImg) {
        setFromPreviewOld(firstReading.fromReadingImg);
        setFromPreview(
          `${configSettings.mediaURL}/get/vehicleReading/${firstReading.fromReadingImg}`
        );
      }

      if (firstReading?.toReadingImg) {
        setToPreviewOld(firstReading.toReadingImg);
        setToPreview(
          `${configSettings.mediaURL}/get/vehicleReading/${firstReading.toReadingImg}`
        );
      }
    } catch (err) {
      console.error("Edit Load Error:", err);
    } finally {
      setEditLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      setEditLoading(true);
      setEditMode(true);
      setReadingId(id);
    }
    loadEditData();
  }, [id]);

  const handleFileUpload = (e, setPreview, fieldName) => {
    utility.readFile(e, setPreview, fieldName);
  };

  const onSubmit = async (formDataRaw) => {
    setLoading(true);
    try {
      const companyId = StorageService.activeCompany().companyId;
      const lastCheck = await VehicleReadingService.checkLastCompleted(
        formDataRaw.vehicleId,
        formDataRaw.projectId,
        companyId,
        editMode ? readingId : null
      );
      if (formDataRaw.workingStatus === "Working" && lastCheck?.status && !lastCheck.isCompleted) {
        showMessage(lastCheck.message || "The previous reading for this vehicle and project is missing its End Reading.");
        setLoading(false);
        return;
      }

      const readingTypeName = getReadingTypeName(formDataRaw.readingTypeId);
      const isCustomReading = readingTypeName === "Custom";
      const isHourOrKmReading = !isCustomReading;

      if (formDataRaw.workingStatus === "Working" && isCustomReading) {
        const rDate = formDataRaw.readingDate;
        for (let i = 0; i < (formDataRaw.readings || []).length; i++) {
          const r = formDataRaw.readings[i];
          if (!r.startDateTime) {
            showMessage("Please enter Start Date & Time");
            setLoading(false);
            return;
          }
          const startDatePart = String(r.startDateTime).slice(0, 10);
          if (startDatePart !== rDate) {
            showMessage("Start Date & Time must be of the same date as Reading Date");
            setLoading(false);
            return;
          }
          if (r.endDateTime) {
            const endDatePart = String(r.endDateTime).slice(0, 10);
            if (endDatePart !== rDate) {
              showMessage("End Date & Time must be of the same date as Reading Date");
              setLoading(false);
              return;
            }
            if (new Date(r.endDateTime) < new Date(r.startDateTime)) {
              showMessage("End Date & Time must be greater than or equal to Start Date & Time");
              setLoading(false);
              return;
            }
            if (new Date(r.endDateTime) > new Date()) {
              showMessage("End Date & Time cannot be greater than current date & time");
              setLoading(false);
              return;
            }
          }
        }
      }

      const cleanedReadings = (formDataRaw.readings || []).map((r) => {
        const cleaned = { ...r };
        if (isNotWorking) {
          cleaned.fromReading = "";
          cleaned.toReading = "";
          cleaned.startDateTime = "";
          cleaned.endDateTime = "";
        } else if (isCustomReading) {
          cleaned.fromReading = "";
          cleaned.toReading = "";
        } else if (isHourOrKmReading) {
          cleaned.startDateTime = "";
          cleaned.endDateTime = "";
        }
        return cleaned;
      });

      const fd = new FormData();
      fd.append("companyId", StorageService.activeCompany().companyId);
      fd.append("readingDate", formDataRaw.readingDate);
      fd.append("workingStatus", formDataRaw.workingStatus);
      fd.append("projectId", formDataRaw.projectId);
      fd.append("vehicleId", formDataRaw.vehicleId);
      fd.append("readingTypeId", formDataRaw.readingTypeId);
      fd.append("readings", JSON.stringify(cleanedReadings));
      fd.append("roleId", Number(StorageService.loginData()?.roleId));

      fd.append("workDescription", formDataRaw.workDescription || "");
      fd.append("driverName", formDataRaw.driverName || "");

      if (isCustomReading || readingTypeName === "Hour") {
        fd.append("startDateTime", cleanedReadings[0]?.startDateTime || "");
        fd.append("endDateTime", cleanedReadings[0]?.endDateTime || "");
      }

      fd.append("fromReadingImg", fromPreview);
      fd.append("toReadingImg", toPreview);
      fd.append("fromReadingImgOld", fromPreviewOld);
      fd.append("toReadingImgOld", toPreviewOld);

      fd.append("status", "Active");
      fd.append("createdBy", StorageService.loginData().employeeId);
      fd.append("updatedBy", StorageService.loginData().employeeId);

      const res = editMode
        ? await VehicleReadingService.update(readingId, fd)
        : await VehicleReadingService.create(fd);

      if (res?.status) {
        showMessage(
          editMode
            ? "Vehicle Reading updated successfully."
            : "Vehicle Reading added successfully.",
          "success"
        );
        setTimeout(() => navigate("/cpnl/vehiclereading/view"), 800);
      } else {
        showMessage(res?.message || "Failed to save Vehicle Reading.");
      }
    } catch (err) {
      console.error(err);
      const errorMsg =
        err?.response?.data?.message ||
        err?.message ||
        "Error while saving Vehicle Reading.";
      showMessage(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const addRow = () => {
    const currentReadings = watch("readings") || [];
    if (currentReadings.length > 0) {
      const latestReading = currentReadings[0];
      const readingTypeName = getReadingTypeName(readingTypeId);
      const isCustomReading = readingTypeName === "Custom";

      if (isCustomReading) {
        if (!latestReading.endDateTime) {
          showMessage("Please enter End Date & Time for the current reading before adding a new one.");
          return;
        }
      } else {
        if (!latestReading.toReading) {
          showMessage("Please enter End Reading for the current reading before adding a new one.");
          return;
        }
      }
    }

    const rDate = watch("readingDate") || getValues("readingDate") || today;
    prepend({
      fromReading: "",
      toReading: "",
      startDateTime: getDateTimeForDate(rDate),
      endDateTime: "",
    });
  };

  const deleteRow = (index) => {
    remove(index);
  };

  const handleCancel = () => navigate("/cpnl/vehiclereading/view");

  const selectedVehicleId = watch("vehicleId");
  const selectedVehicle = vehicles.find((v) => String(v.id) === String(selectedVehicleId));
  const readingDate = watch("readingDate");

  useEffect(() => {
    checkDuplicateReading(selectedVehicleId, readingDate, workingStatus);
  }, [selectedVehicleId, readingDate, workingStatus, vehicles]);

  useEffect(() => {
    checkLastReadingStatus(selectedVehicleId, selectedProjectId);
  }, [selectedVehicleId, selectedProjectId, workingStatus]);

  const filteredReadingTypes = selectedVehicle
    ? readingTypes.filter(
      (rt) =>
        rt.name?.toLowerCase() === selectedVehicle.readingType?.toLowerCase() ||
        rt.name?.toLowerCase() === "custom"
    )
    : readingTypes;

  const readingTypeName = getReadingTypeName(readingTypeId);
  const isCustomReading = readingTypeName === "Custom";
  const isHourOrKmReading = !isCustomReading;

  return (
    <>
      <Breadcrumb
        pageTitle={editMode ? "Modify Vehicle Reading" : "Add Vehicle Reading"}
        secondTitle="Vehicle Management"
      />

      <div className="row">
        <div className="card py-3 bg-white shadow-sm">
          {/* {message && (
            <div
              className={`alert ${
                msgType === "success" ? "alert-success" : "alert-danger"
              } d-flex justify-content-between align-items-center w-100 mb-3 p-2`}
            >
              <span>{message}</span>
              <button className="btn-close" onClick={closeMessage}></button>
            </div>
          )} */}


          <MessageDialog
            message={message}
            type={msgType}
            setMessage={setMessage}
            setMsgType={setMsgType}
          />

          {editLoading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary"></div>
              <div className="mt-2 text-muted">
                Loading VehicleReading details...
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} noValidate>
              <div className="row">
                <div className="mb-3 col-md-6">
                  {/* <label className="form-label">
                    Reading Date <span className="text-danger">*</span>
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    {...register("readingDate", {
                      required: "Reading Date is required",
                    })}
                  />
                  {errors.readingDate && (
                    <small className="text-danger">
                      {errors.readingDate.message}
                    </small>
                  )} */}

                  <label className="form-label">
                    Reading Date <span className="text-danger">*</span>
                  </label>
                  <input
                    type="date"
                    className={`form-control ${errors.readingDate ? "is-invalid" : ""}`}
                    {...register("readingDate", {
                      required: "Reading Date is required",
                      validate: (value) => {
                        const selected = value; // yyyy-mm-dd
                        const today = new Date().toLocaleDateString("en-CA"); // yyyy-mm-dd

                        if (selected > today) {
                          return "Future date is not allowed";
                        }

                        const emp = StorageService.loginData();
                        const isPrivileged =
                          emp?.roleName === "Super Admin" ||
                          emp?.roleName === "SuperAdmin" ||
                          emp?.roleName === "Data Admin" ||
                          emp?.roleName === "DataAdmin" ||
                          Number(emp?.roleId) === 1 ||
                          Number(emp?.roleId) === 2;

                        if (!isPrivileged && selected < today) {
                          return "Only Super Admin and Data Admin can select a past date";
                        }
                        return true;
                      }
                    })}
                    onChange={(e) => {
                      const newDate = e.target.value;
                      setValue("readingDate", newDate, { shouldValidate: true });
                      const currentProjectId = getValues("projectId");
                      if (currentProjectId) {
                        fetchVehicles(currentProjectId, newDate);
                      }
                      const currentVehicleId = getValues("vehicleId");
                      if (currentVehicleId) {
                        checkDuplicateReading(currentVehicleId, newDate, getValues("workingStatus"));
                      }
                      // If custom reading, update readings date component to match new readingDate
                      const currentReadings = getValues("readings") || [];
                      if (currentReadings.length > 0) {
                        const updatedReadings = currentReadings.map((r) => {
                          let updatedStart = r.startDateTime;
                          let updatedEnd = r.endDateTime;
                          if (updatedStart && updatedStart.includes("T")) {
                            const timePart = updatedStart.split("T")[1];
                            updatedStart = newDate ? `${newDate}T${timePart}` : "";
                          }
                          if (updatedEnd && updatedEnd.includes("T")) {
                            const timePart = updatedEnd.split("T")[1];
                            updatedEnd = newDate ? `${newDate}T${timePart}` : "";
                          }
                          return {
                            ...r,
                            startDateTime: updatedStart,
                            endDateTime: updatedEnd,
                          };
                        });
                        setValue("readings", updatedReadings, { shouldValidate: true });
                      }
                    }}
                  />
                  {errors.readingDate && (
                    <small className="text-danger">
                      {errors.readingDate.message}
                    </small>
                  )}

                </div>

                {/* Project */}
                <div className="mb-3 col-md-6">
                  <label className="form-label">
                    Project <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    {...register("projectId", {
                      required: "Project is required",
                    })}
                    onChange={(e) => {
                      setValue("projectId", e.target.value, { shouldValidate: true });
                      fetchVehicles(e.target.value, getValues("readingDate"));
                    }}
                  >
                    <option value="">Select Project</option>
                    {projects.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.projectName}-{p.projectCode}
                      </option>
                    ))}
                  </select>
                  {errors.projectId && (
                    <small className="text-danger">
                      {errors.projectId.message}
                    </small>
                  )}

                </div>

                {/* Vehicle */}
                <div className="mb-3 col-md-6">
                  {/* <label className="form-label">
                    Vehicle <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    {...register("vehicleId", { required: true })}
                    onChange={(e) => handleVehicleChange(e.target.value)}
                  >
                    <option value="">Select Vehicle</option>
                    {vehicles.map((v) => (
                      <option key={v.id} value={v.id}>
                        ({v.vehicleType})--- {v.vehicleName}-{v.registrationNo}
                      </option>
                    ))}
                  </select> */}

                  <label className="form-label">
                    Vehicle <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    {...register("vehicleId", {
                      required: "Vehicle is required",
                    })}
                    onChange={(e) => handleVehicleChange(e.target.value)}
                  >
                    <option value="">Select Vehicle</option>
                    {vehicles.map((v) => (
                      <option key={v.id} value={v.id}>
                        ({v.vehicleType})--- {v.vehicleName}-{v.registrationNo}
                      </option>
                    ))}
                  </select>
                  {errors.vehicleId && (
                    <small className="text-danger">
                      {errors.vehicleId.message}
                    </small>
                  )}


                  {selectedProjectId && vehicles.length === 0 && (
                    <p
                      className="text-danger mt-1"
                      style={{ fontSize: "14px" }}
                    >
                      No assigned vehicles for this project
                    </p>
                  )}
                </div>

                {/* Reading Type */}
                <div className="mb-3 col-md-6">
                  <label className="form-label">
                    Reading Type <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    {...register("readingTypeId", {
                      required: "Reading Type is required",
                    })}
                  >
                    <option value="">Select Reading Type</option>
                    {filteredReadingTypes.length > 0 ? (
                      filteredReadingTypes.map((rt) => (
                        <option key={rt.id} value={String(rt.id)}>
                          {rt.name}
                        </option>
                      ))
                    ) : (
                      <option disabled>No Reading Types Found</option>
                    )}
                  </select>
                  {errors.readingTypeId && (
                    <small className="text-danger">
                      {errors.readingTypeId.message}
                    </small>
                  )}
                </div>
              </div>

              <div className="row mt-3">
                <div className="mb-3 col-md-6">
                  <label className="form-label">
                    Working Status <span className="text-danger">*</span>
                  </label>
                  <select
                    className={`form-select ${errors.workingStatus ? "is-invalid" : ""}`}
                    {...register("workingStatus", {
                      required: "Working Status is required",
                    })}
                  >
                    <option value="">Select</option>
                    <option value="Working">Working</option>
                    <option value="Idle">Idle</option>
                    <option value="BreakDown">BreakDown</option>
                  </select>
                  {errors.workingStatus && (
                    <small className="text-danger">
                      {errors.workingStatus.message}
                    </small>
                  )}
                </div>

                <div className="mb-3 col-md-6">
                  <label className="form-label">
                    Work Description {isDescriptionRequired && <span className="text-danger">*</span>}
                  </label>
                  <input
                    type="text"
                    className={`form-control ${errors.workDescription ? "is-invalid" : ""}`}
                    placeholder="Enter work description"
                    {...register("workDescription", {
                      required: isDescriptionRequired ? "Work Description is required when status is Idle or BreakDown" : false
                    })}
                  />
                  {errors.workDescription && (
                    <small className="text-danger">
                      {errors.workDescription.message}
                    </small>
                  )}
                </div>
              </div>

              <div className="row">
                <div className="mb-3 col-md-6">
                  <label className="form-label">
                    Driver Name {workingStatus === "Working" && <span className="text-danger">*</span>}
                  </label>
                  <input
                    type="text"
                    className={`form-control ${errors.driverName ? "is-invalid" : ""}`}
                    placeholder="Enter driver name"
                    {...register("driverName", {
                      required: workingStatus === "Working" ? "Driver Name is required when Working Status is Working" : false
                    })}
                  />
                  {errors.driverName && (
                    <small className="text-danger">
                      {errors.driverName.message}
                    </small>
                  )}
                </div>
              </div>




              {workingStatus === "Working" && (
                <>


                  {/* Readings */}
                  {fields.map((row, idx) => (
                    <div className="row mt-3" key={row.id}>
                      {isHourOrKmReading && (
                        <>
                          <div className="mb-3 col-md-2">
                            <label className="form-label">Start Reading{!isNotWorking && <span className="text-danger">*</span>}</label>
                            <input
                              type="number"
                              step="0.1"
                              className="form-control"
                              disabled={isNotWorking}
                              {...register(`readings.${idx}.fromReading`, {
                                required: (isHourOrKmReading && !isNotWorking) ? "Please enter Start Reading" : false,
                                validate: (value) => {
                                  if (isNotWorking) return true;
                                  if (!isHourOrKmReading) return true;
                                  if (!/^\d*\.?\d?$/.test(value)) {
                                    return "Only one digit allowed after decimal";
                                  }
                                  const readingsList = watch("readings") || [];
                                  if (idx < readingsList.length - 1) {
                                    const prevRowToVal = watch(`readings.${idx + 1}.toReading`);
                                    if (prevRowToVal === "" || prevRowToVal === undefined || prevRowToVal === null) {
                                      return "Cannot start a new reading because the previous reading is missing its End Reading.";
                                    }
                                    const nextRowTo = parseFloat(prevRowToVal) || 0;
                                    const currentFrom = parseFloat(value) || 0;
                                    if (currentFrom < nextRowTo) {
                                      return `Start Reading must be greater than or equal to previous End Reading (${nextRowTo})`;
                                    }
                                  }
                                  return true;
                                }
                              })}
                            />
                            {errors.readings?.[idx]?.fromReading && (
                              <small className="text-danger">
                                {errors.readings[idx].fromReading.message}
                              </small>
                            )}
                          </div>

                          <div className="mb-3 col-md-2">
                            <label className="form-label">End Reading</label>
                            <input
                              type="number"
                              step="0.1"
                              className="form-control"
                              disabled={isNotWorking}
                              {...register(`readings.${idx}.toReading`, {
                                validate: (value) => {
                                  if (isNotWorking) return true;
                                  if (!isHourOrKmReading) return true;
                                  if (!value) return true;
                                  if (!/^\d*\.?\d?$/.test(value)) {
                                    return "Only one digit allowed after decimal";
                                  }
                                  const from = parseFloat(watch(`readings.${idx}.fromReading`)) || 0;
                                  const to = parseFloat(value);
                                  if (to <= from) {
                                    return "End Reading must be greater than Start Reading";
                                  }
                                  if (idx > 0) {
                                    const prevRowFrom = parseFloat(watch(`readings.${idx - 1}.fromReading`)) || 0;
                                    if (to > prevRowFrom) {
                                      return `End Reading must be less than or equal to next Start Reading (${prevRowFrom})`;
                                    }
                                  }
                                  return true;
                                },
                              })}
                            />
                            {errors.readings?.[idx]?.toReading && (
                              <small className="text-danger">
                                {errors.readings[idx].toReading.message}
                              </small>
                            )}
                          </div>

                          <div className="mb-3 col-md-2">
                            <label className="form-label">Total Vehicle Usage</label>
                            <label className="form-control">
                              {(() => {
                                const from = parseFloat(watch(`readings.${idx}.fromReading`)) || 0;
                                const to = parseFloat(watch(`readings.${idx}.toReading`)) || 0;
                                const total = to - from;
                                return total > 0 ? total.toFixed(1) : "0.0";
                              })()}
                            </label>
                          </div>
                        </>
                      )}

                      {isCustomReading && (
                        <>
                          <div className="mb-3 col-md-3">
                            <label className="form-label">
                              Start Date & Time {!isNotWorking && <span className="text-danger">*</span>}
                            </label>
                            <input
                              type="datetime-local"
                              disabled={isNotWorking}
                              className={`form-control ${errors.readings?.[idx]?.startDateTime ? "is-invalid" : ""}`}
                              min={readingDate ? `${readingDate}T00:00` : undefined}
                              max={readingDate ? (readingDate === today ? getNowLocalDateTime() : `${readingDate}T23:59`) : undefined}
                              {...register(`readings.${idx}.startDateTime`, {
                                required: (isCustomReading && !isNotWorking) ? "Please enter Start Date & Time" : false,
                                validate: (value) => {
                                  if (isNotWorking) return true;
                                  if (!isCustomReading) return true;
                                  if (!value) return "Please enter Start Date & Time";

                                  const rDate = watch("readingDate") || getValues("readingDate");
                                  if (rDate) {
                                    const startDatePart = String(value).slice(0, 10);
                                    if (startDatePart !== rDate) {
                                      return "Start Date & Time must be of the same date as Reading Date";
                                    }
                                  }

                                  const readingsList = watch("readings") || [];
                                  if (idx < readingsList.length - 1) {
                                    const prevRowEndVal = watch(`readings.${idx + 1}.endDateTime`);
                                    if (!prevRowEndVal) {
                                      return "Cannot start a new reading because the previous reading is missing its End Date & Time.";
                                    }
                                    const prevEnd = new Date(prevRowEndVal);
                                    const currentStart = new Date(value);
                                    if (currentStart < prevEnd) {
                                      return "Start Date & Time must be greater than or equal to previous End Date & Time";
                                    }
                                  }
                                  return true;
                                }
                              })}
                            />
                            {errors.readings?.[idx]?.startDateTime && (
                              <small className="text-danger">
                                {errors.readings[idx].startDateTime.message}
                              </small>
                            )}
                          </div>

                          <div className="mb-3 col-md-3">
                            <label className="form-label">End Date & Time</label>
                            <input
                              type="datetime-local"
                              disabled={isNotWorking}
                              className={`form-control ${errors.readings?.[idx]?.endDateTime ? "is-invalid" : ""}`}
                              min={watch(`readings.${idx}.startDateTime`) || (readingDate ? `${readingDate}T00:00` : undefined)}
                              max={readingDate ? (readingDate === today ? getNowLocalDateTime() : `${readingDate}T23:59`) : undefined}
                              {...register(`readings.${idx}.endDateTime`, {
                                validate: (value) => {
                                  if (isNotWorking) return true;
                                  if (!isCustomReading) return true;
                                  if (!value) return true;

                                  const rDate = watch("readingDate") || getValues("readingDate");
                                  if (rDate) {
                                    const endDatePart = String(value).slice(0, 10);
                                    if (endDatePart !== rDate) {
                                      return "End Date & Time must be of the same date as Reading Date";
                                    }
                                  }

                                  const now = new Date();
                                  const end = new Date(value);

                                  const startVal = watch(`readings.${idx}.startDateTime`);
                                  if (startVal) {
                                    const start = new Date(startVal);
                                    if (end < start) {
                                      return "End Date & Time must be greater than or equal to Start Date & Time";
                                    }
                                  }

                                  return (
                                    end <= now ||
                                    "End Date & Time cannot be greater than current date & time"
                                  );
                                },
                              })}
                            />
                            {errors.readings?.[idx]?.endDateTime && (
                              <small className="text-danger">
                                {errors.readings[idx].endDateTime.message}
                              </small>
                            )}
                          </div>
                        </>
                      )}

                      <div className="mb-3 col-md-2">
                        <label className="form-label">&nbsp;</label>
                        <div className="d-flex gap-2">
                          <button
                            type="button"
                            className="btn btn-success btn-sm"
                            onClick={addRow}
                            disabled={isNotWorking}
                          >
                            +
                          </button>
                          {fields.length > 1 && (
                            <button
                              type="button"
                              className="btn btn-danger btn-sm"
                              onClick={() => deleteRow(idx)}
                              disabled={isNotWorking}
                            >
                              -
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Images */}
                  <div className="row">
                    <div className="mb-3 col-md-6">
                      <label className="form-label">Start Reading Image</label>
                      <input
                        type="file"
                        accept="image/*"
                        className="form-control"
                        onChange={(e) =>
                          handleFileUpload(e, setFromPreview, "fromReadingImg")
                        }
                      />

                      {fromPreview && (
                        <img
                          src={fromPreview}
                          width="120"
                          height="100"
                          className="mt-2 rounded"
                          style={{ objectFit: "cover" }}
                          alt=""
                        />
                      )}
                    </div>

                    <div className="mb-3 col-md-6">
                      <label className="form-label">End Reading Image</label>
                      <input
                        type="file"
                        accept="image/*"
                        className="form-control"
                        onChange={(e) =>
                          handleFileUpload(e, setToPreview, "toReadingImg")
                        }
                      />

                      {toPreview && (
                        <img
                          src={toPreview}
                          width="120"
                          height="100"
                          className="mt-2 rounded"
                          style={{ objectFit: "cover" }}
                          alt=""
                        />
                      )}
                    </div>
                  </div>
                </>
              )}

              {/* Buttons */}
              <div className="mt-3">
                <button
                  type="submit"
                  className="btn btn-primary me-2"
                  disabled={loading || !!statusMismatchError || (workingStatus === "Working" && !!lastReadingError)}
                >
                  <i className="ti ti-circle-check me-1"></i>
                  {loading ? "Saving..." : editMode ? "Update" : "Save"}
                </button>

                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCancel}
                  disabled={loading}
                >
                  <i className="ti ti-cancel me-1"></i>
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </>
  );
}